import {
  SPECIAL_PRICE_ATTRIBUTE_TITLES,
  SPECIAL_PRICE_SCOPE_QUALIFIER_KEYS
} from '../constant/constantspecial'
import {
  SPECIAL_PRICE_DEFAULT_PER_PAGE,
  SPECIAL_PRICE_EMPTY_SELECT_FILTER,
  SPECIAL_PRICE_FILTER_QUERY_KEYS,
  SPECIAL_PRICE_FILTERS_INITIAL_STATE
} from '../constant/specialPriceViewConstants'
import { getStatusConfig } from './specialPriceHelpers'

const parseMultiFilterValue = (searchParams, key) =>
  (searchParams.get(key) || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

const sortByLabel = (left, right) =>
  String(left).localeCompare(String(right), 'es', { sensitivity: 'base' })

const encodeQueryValuePreservingCommas = (value) =>
  encodeURIComponent(String(value)).replace(/%2C/gi, ',')

const appendQueryParam = (queryParts, key, value) => {
  if (value === undefined || value === null || value === '') return

  queryParts.push(`${encodeURIComponent(key)}=${encodeQueryValuePreservingCommas(value)}`)
}

const setDateRangeParams = (queryParts, dateRange) => {
  if (Array.isArray(dateRange) && dateRange[0]) {
    appendQueryParam(queryParts, 'date_from', formatDateRangeValue(dateRange[0]))
  }

  if (Array.isArray(dateRange) && dateRange[1]) {
    appendQueryParam(queryParts, 'date_to', formatDateRangeValue(dateRange[1]))
  }
}

const setPaginationParams = (
  queryParts,
  pagination = {},
  defaultPerPage = SPECIAL_PRICE_DEFAULT_PER_PAGE
) => {
  if (pagination?.current_page && Number(pagination.current_page) > 1) {
    appendQueryParam(queryParts, 'page', String(pagination.current_page))
  }

  if (pagination?.per_page && Number(pagination.per_page) !== defaultPerPage) {
    appendQueryParam(queryParts, 'per_page', String(pagination.per_page))
  }
}

const getScopeGroupConfig = (scopeKey) => {
  if (scopeKey === 'clients') {
    return {
      group: 'clients',
      groupLabel: 'Cliente individual'
    }
  }

  const qualifierIndex = SPECIAL_PRICE_SCOPE_QUALIFIER_KEYS.indexOf(scopeKey)
  if (qualifierIndex < 0) return null

  return {
    group: scopeKey,
    groupLabel: SPECIAL_PRICE_ATTRIBUTE_TITLES[qualifierIndex] || `Atributo ${qualifierIndex + 1}`
  }
}

export const getSpecialPriceFiltersFromSearch = (search = '') => {
  const searchParams = new URLSearchParams(search)
  const dateFrom = searchParams.get('date_from')
  const dateTo = searchParams.get('date_to')
  const dateRange = [dateFrom, dateTo].filter(Boolean)

  return {
    ...SPECIAL_PRICE_FILTERS_INITIAL_STATE,
    client_attribute: parseMultiFilterValue(searchParams, 'client_attribute'),
    product: parseMultiFilterValue(searchParams, 'product'),
    sales_unit: parseMultiFilterValue(searchParams, 'sales_unit'),
    status: parseMultiFilterValue(searchParams, 'status'),
    date_range: dateRange.length > 0 ? dateRange : null,
    q: searchParams.get('q') || ''
  }
}

export const getSpecialPriceLocationState = (
  search = '',
  defaultPerPage = SPECIAL_PRICE_DEFAULT_PER_PAGE
) => {
  const searchParams = new URLSearchParams(search)

  return {
    filters: getSpecialPriceFiltersFromSearch(search),
    searchInput: searchParams.get('q') || '',
    currentPage: Number(searchParams.get('page') || 1),
    perPage: Number(searchParams.get('per_page') || defaultPerPage)
  }
}

export const formatDateRangeValue = (value) =>
  value?.format ? value.format('YYYY-MM-DD') : String(value)

export const areFiltersEqual = (left, right) => JSON.stringify(left || {}) === JSON.stringify(right || {})

export const toFilterSelectValue = (values = []) =>
  Array.isArray(values) && values.length > 0
    ? {
        mode: 'custom',
        values
      }
    : SPECIAL_PRICE_EMPTY_SELECT_FILTER

export const serializeMultiFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(',')
  }

  return value || ''
}

export const normalizeOptionItems = (values = [], formatter, shouldSort = true) => {
  const normalizedValues = values
    .map((value) => {
      if (value && typeof value === 'object' && 'value' in value && 'label' in value) {
        return value
      }

      return formatter
        ? formatter(value)
        : {
            label: value,
            value
          }
    })
    .filter((option) => option?.value && option?.label)

  const uniqueValues = Array.from(
    new Map(
      normalizedValues.map((option) => {
        const groupKey = option.group || option.group_key || ''
        const optionValue = option.value
        const optionLabel = option.label

        return [`${groupKey}:${optionValue}:${optionLabel}`, option]
      })
    ).values()
  )

  if (shouldSort) {
    uniqueValues.sort((left, right) => sortByLabel(left.label, right.label))
  }

  return uniqueValues
}

export const buildOptions = (values = [], formatter, shouldSort = true) => {
  const uniqueValues = Array.from(new Set(values.filter(Boolean)))

  if (shouldSort) {
    uniqueValues.sort(sortByLabel)
  }

  return uniqueValues.map((value) =>
    formatter
      ? formatter(value)
      : {
          label: value,
          value
        }
  )
}

export const normalizeScopeOptions = (scopes) => {
  if (Array.isArray(scopes)) {
    return normalizeOptionItems(scopes, (value) => ({
      label: value,
      value: String(value || '')
        .replace(/^Atributo\s+\d+\s*:\s*/i, '')
        .split(' - ')[0]
        .trim()
    }))
  }

  if (!scopes || typeof scopes !== 'object') return []

  return Object.entries(scopes).flatMap(([scopeKey, items]) => {
    const groupConfig = getScopeGroupConfig(scopeKey)
    if (!groupConfig || !Array.isArray(items)) return []

    return normalizeOptionItems(
      items.map((item) => ({
        ...item,
        group: groupConfig.group,
        groupLabel: groupConfig.groupLabel
      })),
      null,
      true
    )
  })
}

export const buildSpecialPriceFilterOptions = (filterInfo) => ({
  client_attribute: normalizeScopeOptions(filterInfo?.scopes),
  product: normalizeOptionItems(filterInfo?.products, (value) => ({
    label: value,
    value: String(value || '').split(' - ')[0].trim()
  })),
  sales_unit: buildOptions(filterInfo?.units),
  status: buildOptions(
    filterInfo?.statuses,
    (status) => ({
      label: getStatusConfig(status).label,
      value: status
    }),
    false
  )
})

export const hasActiveFilters = (filters, searchInput = '') => {
  const hasDateRange = Array.isArray(filters?.date_range) && filters.date_range.length > 0

  return (
    hasDateRange ||
    filters?.client_attribute?.length > 0 ||
    filters?.product?.length > 0 ||
    filters?.sales_unit?.length > 0 ||
    filters?.status?.length > 0 ||
    Boolean(filters?.q) ||
    Boolean(searchInput)
  )
}

export const buildSearchQuery = ({
  filters,
  searchInput = '',
  pagination,
  defaultPerPage = SPECIAL_PRICE_DEFAULT_PER_PAGE
}) => {
  const queryParts = []
  const normalizedFilters = {
    ...filters,
    q: filters?.q ?? searchInput ?? ''
  }

  SPECIAL_PRICE_FILTER_QUERY_KEYS.forEach((key) => {
    const serializedValue = serializeMultiFilterValue(normalizedFilters[key])

    if (serializedValue) {
      appendQueryParam(queryParts, key, serializedValue)
    }
  })

  setDateRangeParams(queryParts, normalizedFilters.date_range)
  setPaginationParams(queryParts, pagination, defaultPerPage)

  return queryParts.join('&')
}

export const buildRequestQuery = ({ filters, pagination }) => {
  const queryParts = []

  appendQueryParam(queryParts, 'page', pagination.current_page)
  appendQueryParam(queryParts, 'per_page', pagination.per_page)

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'date_range') return

    const serializedValue = serializeMultiFilterValue(value)
    if (serializedValue) {
      appendQueryParam(queryParts, key, serializedValue)
    }
  })

  setDateRangeParams(queryParts, filters.date_range)

  return queryParts.join('&')
}

export const normalizeFilterInfoResponse = (response) =>
  response?.filter || response?.table || response?.result || response || {}
